import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App.jsx';
import '@styles/index.css';

// Load brand config
const BRAND = import.meta.env.VITE_GAME_BRAND;
if (!BRAND) {
  throw new Error('VITE_GAME_BRAND environment variable is not set');
}
console.log(`[🎨 brand] Loaded: ${BRAND}`);
// Note: Theme CSS is loaded by Asset.init() from public/{brand}/theme/{themename}/

// Load brand config and set page title
const brandConfig = (await import(`@/brands/${BRAND}/brand.config.js`)).default;
document.title = brandConfig.pageTitle || `${brandConfig.name} - Strategic Warfare`;

// Initialize Storage (required before any storage operations)
import { Storage } from '@storage';
await Storage.init();

// Fetch resource versions for cache busting (lightweight ~100 bytes)
// This tells us if translations/metadata need to be re-fetched
import api from '@api/api';
let remoteVersions = null;
try {
  const response = await api.get('/api/versions');
  remoteVersions = response?.data || null;
  console.log('[📦 versions] Remote:', remoteVersions);
} catch (error) {
  console.warn('[📦 versions] Failed to fetch, will force refresh all:', error.message);
}

// Load user config from IndexedDB first (persists settings across page reloads)
import useConfigStore from '@/store/useConfigStore';
await useConfigStore.getState().init();

// Initialize Asset system with saved theme (or 'base' as default)
// NOTE: Must load BEFORE translations (i18n needs Asset.getSource())
import { Asset } from '@rts';
const savedTheme = useConfigStore.getState().settings.theme || 'base';
await Asset.init(`/${BRAND}/theme/${savedTheme}/theme.json`);

// Load translations from IndexedDB or fetch from backend
// Pass remote version to compare with cache and avoid unnecessary fetches
import useTranslationStore from '@/store/useTranslationStore';
await useTranslationStore.getState().init(remoteVersions?.translations);
console.log(`[🌐 i18n] Locale: ${useTranslationStore.getState().locale}`);

// Preload assets from theme.json preload config (music, sounds, images)
const themePreload = Asset.getTheme().preload;
if (themePreload) {
  const music = themePreload.music || [];
  const sounds = themePreload.sounds || [];
  const images = themePreload.images || [];
  const totalAssets = music.length + sounds.length + images.length;
  if (totalAssets > 0) {
    console.log(`[🔊 assets] Preloading: ${music.length} music, ${sounds.length} sounds, ${images.length} images...`);
    Asset.preload({ label: 'Preloading theme assets', music, sounds, images });
    await Asset.waitForComplete();
    console.log(`[🔊 assets] Preloaded: ${totalAssets} assets`);
  }
}

// Restore metadata from IndexedDB if available (speeds up page reload)
// Pass remote version to compare with cache and avoid unnecessary fetches
import useMetadataStore from '@/store/useMetadataStore';
await useMetadataStore.getState().init(remoteVersions?.metadata);

// Initialize debug tools (from develop)
import { initDebugHelpers } from './utils/debugMerged';
initDebugHelpers();

// Crear instancia única de QueryClient
const queryClient = new QueryClient();

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
